import { Check, GitBranch, RotateCcw, Server, Users, Wrench } from 'lucide-react'
import { useEffect, useState } from 'react'

import { getApiBaseUrl, isDevToolsEnabled } from '@/config/api-base-url'
import { env } from '@/config/env'
import {
  loginAsTestUser,
  switchEnvironment,
  TEST_USER_PASSWORD,
  useBrandUsers,
  useReviewApps,
} from '@/domain/dev-tools/dev-tools.hooks'
import { Badge } from '@/ui/components/badge'
import { Button } from '@/ui/components/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/ui/components/dialog'
import { Input } from '@/ui/components/input'

type Tab = 'environments' | 'users'

export function DevToolsWidget() {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<Tab>('environments')
  const [activeUrl, setActiveUrl] = useState(getApiBaseUrl)

  // Never render in production — the dev tools and their API key are staging-only.
  if (!isDevToolsEnabled) return null

  // Switching env is the intermediate step: apply it, then move on to picking a
  // brand user (the final step) without closing the modal. Reset stays put.
  const applyEnvironment = (url: string, advance: boolean) => {
    switchEnvironment(url)
    setActiveUrl(url)
    if (advance) setTab('users')
  }

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true)
        }}
        title="Dev tools"
        aria-label="Open dev tools"
        className="fixed bottom-4 right-4 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-foreground text-background shadow-lg transition-transform hover:scale-105"
      >
        <Wrench className="h-5 w-5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Dev Tools</DialogTitle>
            <DialogDescription>
              Switch API environment and sign in as a test user. Staging only.
            </DialogDescription>
          </DialogHeader>

          {!env.VITE_REVIEW_APPS_API_KEY && (
            <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
              <code>VITE_REVIEW_APPS_API_KEY</code> is not configured for this build, so the admin
              endpoints are unavailable.
            </p>
          )}

          <div className="flex gap-2">
            <TabButton
              active={tab === 'environments'}
              onClick={() => {
                setTab('environments')
              }}
            >
              <Server className="h-4 w-4" /> Environments
            </TabButton>
            <TabButton
              active={tab === 'users'}
              onClick={() => {
                setTab('users')
              }}
            >
              <Users className="h-4 w-4" /> Test Users
            </TabButton>
          </div>

          {tab === 'environments' ? (
            <EnvironmentsPanel
              activeUrl={activeUrl}
              onSelect={(url) => {
                applyEnvironment(url, true)
              }}
              onReset={() => {
                applyEnvironment(env.VITE_API_BASE_URL, false)
              }}
            />
          ) : (
            <TestUsersPanel baseUrl={activeUrl} />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? 'border-transparent bg-primary text-primary-foreground'
          : 'border-border bg-transparent text-muted-foreground hover:bg-accent'
      }`}
    >
      {children}
    </button>
  )
}

function EnvironmentsPanel({
  activeUrl,
  onSelect,
  onReset,
}: {
  activeUrl: string
  onSelect: (url: string) => void
  onReset: () => void
}) {
  const { data, isLoading, isError, error } = useReviewApps()

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2 rounded-md bg-muted p-3 text-xs">
        <span className="truncate">
          <span className="font-semibold">Active API:</span> {activeUrl}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          title="Reset to the default environment"
        >
          <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset
        </Button>
      </div>

      {isLoading && <PanelMessage>Loading environments…</PanelMessage>}
      {isError && <PanelMessage variant="error">{errorMessage(error)}</PanelMessage>}
      {data?.data.length === 0 && <PanelMessage>No review apps available.</PanelMessage>}

      <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto">
        {data?.data.map((reviewApp) => {
          const selected = reviewApp.urls.api === activeUrl
          return (
            <li key={reviewApp.name}>
              <button
                type="button"
                onClick={() => {
                  onSelect(reviewApp.urls.api)
                }}
                className={`flex w-full flex-col gap-1 rounded-lg border p-3 text-left transition-colors ${
                  selected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 hover:bg-accent'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{reviewApp.name}</span>
                  {selected && <Check className="h-4 w-4 text-primary" />}
                </div>
                {reviewApp.branch && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <GitBranch className="h-3 w-3" /> {reviewApp.branch}
                  </span>
                )}
                <span className="truncate text-xs text-muted-foreground">{reviewApp.urls.api}</span>
                {reviewApp.comment && (
                  <span className="text-xs text-muted-foreground">{reviewApp.comment}</span>
                )}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function TestUsersPanel({ baseUrl }: { baseUrl: string }) {
  const [input, setInput] = useState('')
  const [search, setSearch] = useState('')

  // Debounce the search so each keystroke doesn't fire a request.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(input)
    }, 300)
    return () => {
      clearTimeout(timer)
    }
  }, [input])

  const { data, isLoading, isError, error, isFetching } = useBrandUsers(search, baseUrl)
  const users = data?.data ?? []

  return (
    <div className="flex flex-col gap-3">
      <Input
        autoFocus
        placeholder="Search by email…"
        value={input}
        onChange={(e) => {
          setInput(e.target.value)
        }}
      />

      {(isLoading || isFetching) && <PanelMessage>Searching…</PanelMessage>}
      {isError && <PanelMessage variant="error">{errorMessage(error)}</PanelMessage>}
      {!isFetching && search && users.length === 0 && (
        <PanelMessage>No users found for “{search}”.</PanelMessage>
      )}

      <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto">
        {users.map((user) => {
          // Invite-pending seed users have a random password and can't be logged
          // in as, so they're shown disabled.
          const invitePending = user.first_sign_in_at === null
          return (
            <li key={String(user.id)}>
              <button
                type="button"
                disabled={invitePending}
                onClick={() => {
                  loginAsTestUser(user.email)
                }}
                className="flex w-full flex-col gap-1 rounded-lg border border-border p-3 text-left transition-colors enabled:hover:border-primary/50 enabled:hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="font-semibold">{user.full_name ?? user.email}</span>
                {user.full_name && (
                  <span className="text-xs text-muted-foreground">{user.email}</span>
                )}
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="text-[10px]">
                    {user.role}
                  </Badge>
                  {user.company && (
                    <Badge variant="secondary" className="text-[10px]">
                      {user.company.name} · {user.company.brand_dashboard_plan}
                    </Badge>
                  )}
                  {invitePending && (
                    <Badge variant="outline" className="text-[10px]">
                      invite pending
                    </Badge>
                  )}
                </div>
              </button>
            </li>
          )
        })}
      </ul>

      <p className="text-xs text-muted-foreground">
        Selecting a user prefills the login email and password (<code>{TEST_USER_PASSWORD}</code>) —
        just hit sign in.
      </p>
    </div>
  )
}

function PanelMessage({
  children,
  variant = 'default',
}: {
  children: React.ReactNode
  variant?: 'default' | 'error'
}) {
  return (
    <p
      className={`rounded-md p-3 text-sm ${
        variant === 'error' ? 'bg-destructive/10 text-destructive' : 'text-muted-foreground'
      }`}
    >
      {children}
    </p>
  )
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong.'
}
